import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus, Search, Send, Package, MapPin, User, Users, Trash2, X, HardHat, DollarSign, Signature, Check, ChevronLeft } from 'lucide-react';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { toast } from 'sonner';
import AccessRestricted from '../../components/AccessRestricted';

const CreateIssuance: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        locationId: '',
        workerType: 'Field Worker' as 'Field Worker' | 'System User',
        workerId: '',
        notes: '',
        items: [] as any[],
        signatureType: 'Type' as 'Draw' | 'Type',
        signatureData: ''
    });

    const [typedSignature, setTypedSignature] = useState('');
    const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);

    const [locations, setLocations] = useState<any[]>([]);
    const [fieldWorkers, setFieldWorkers] = useState<any[]>([]);
    const [systemUsers, setSystemUsers] = useState<any[]>([]);
    const [inventoryItems, setInventoryItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showItemResults, setShowItemResults] = useState(false);

    useEffect(() => {
        const fetchMeta = async () => {
            setLoading(true);
            try {
                const [locs, workers, users] = await Promise.all([
                    api.get('/api/inventory/locations'),
                    api.get('/api/inventory/workers'),
                    api.get('/api/users?all_users=true')
                ]);
                setLocations(locs);
                setFieldWorkers(workers);
                setSystemUsers(users.users || users);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchMeta();
    }, []);

    useEffect(() => {
        const fetchItems = async () => {
            if (!formData.locationId) {
                setInventoryItems([]);
                return;
            }
            try {
                const items = await api.get(`/api/inventory/items?locationId=${formData.locationId}&status=Active`);
                setInventoryItems(items);
            } catch (err) {
                console.error(err);
            }
        };
        fetchItems();
    }, [formData.locationId]);

    const addItem = (item: any) => {
        if (formData.items.find(i => i.inventoryItemId === item.id)) {
            toast.error('Item already added');
            return;
        }

        setFormData({
            ...formData,
            items: [
                ...formData.items,
                {
                    inventoryItemId: item.id,
                    name: item.name,
                    unitCost: parseFloat(item.costPerUnit) || 0,
                    quantity: 1,
                    availableStock: item.currentStock,
                    category: item.category,
                    expiryDays: item.defaultLifespanDays || 0
                }
            ]
        });
        setSearchTerm('');
        setShowItemResults(false);
    };

    const removeItem = (index: number) => {
        const newItems = [...formData.items];
        newItems.splice(index, 1);
        setFormData({ ...formData, items: newItems });
    };

    const updateQuantity = (index: number, delta: number) => {
        const newItems = [...formData.items];
        const newQty = (newItems[index].quantity || 0) + delta;
        setQuantity(index, newQty);
    };

    const setQuantity = (index: number, value: number) => {
        const newItems = [...formData.items];
        let newQty = value;

        if (newQty < 1) newQty = 1;
        if (newQty > newItems[index].availableStock) {
            toast.error(`Exceeds available stock (${newItems[index].availableStock})`);
            newQty = newItems[index].availableStock;
        }

        newItems[index].quantity = newQty;
        setFormData({ ...formData, items: newItems });
    };

    const updateExpiryDays = (index: number, delta: number) => {
        const newItems = [...formData.items];
        const newDays = Math.max(0, (newItems[index].expiryDays || 0) + delta);
        setExpiryDays(index, newDays);
    };

    const setExpiryDays = (index: number, value: number) => {
        const newItems = [...formData.items];
        newItems[index].expiryDays = Math.max(0, value);
        setFormData({ ...formData, items: newItems });
    };

    const calculateTotals = () => {
        const totalItems = formData.items.reduce((sum, i) => sum + i.quantity, 0);
        const totalValue = formData.items.reduce((sum, i) => sum + (i.quantity * i.unitCost), 0);
        return { totalItems, totalValue };
    };

    const handleSubmit = async () => {
        if (!formData.locationId || !formData.workerId || formData.items.length === 0) {
            toast.error('Please fill in all required fields and add at least one item');
            return;
        }

        try {
            setLoading(true);
            const { totalItems, totalValue } = calculateTotals();
            const payload = {
                ...formData,
                totalItems,
                totalValue
            };
            await api.post('/api/inventory/issuances', payload);
            toast.success('Issuance created successfully');
            navigate('/inventory');
        } catch (err: any) {
            toast.error(err.message || 'Failed to create issuance');
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = inventoryItems.filter(i =>
        (i.status === 'Active' && !i.archived) && (
            i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            i.sku.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    const { totalItems, totalValue } = calculateTotals();

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
        setIsDrawing(true);
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const rect = canvas.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const rect = canvas.getBoundingClientRect();
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.stroke();
    };

    const stopDrawing = () => setIsDrawing(false);

    const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const touch = e.touches[0];
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        setIsDrawing(true);
        ctx.beginPath();
        ctx.moveTo(
            (touch.clientX - rect.left) * scaleX,
            (touch.clientY - rect.top) * scaleY
        );
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const touch = e.touches[0];
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        ctx.lineTo(
            (touch.clientX - rect.left) * scaleX,
            (touch.clientY - rect.top) * scaleY
        );
        ctx.stroke();
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const handleConfirmSignature = () => {
        if (formData.signatureType === 'Type') {
            if (!typedSignature.trim()) {
                toast.error('Please enter your name');
                return;
            }
            setFormData({ ...formData, signatureData: typedSignature.trim() });
        } else {
            if (canvasRef.current) {
                const dataUrl = canvasRef.current.toDataURL();
                setFormData({ ...formData, signatureData: dataUrl });
            }
        }
        setIsSignatureDialogOpen(false);
    };

    if (!ability.can(ACTIONS.CREATE, RESOURCES.INVENTORY_ISSUANCE)) {
        return <AccessRestricted navigateTo="/inventory" backMessage="Back to Inventory" />;
    }

    return (
        <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <Button
                    variant="outline"
                    size="icon"
                    className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
                    onClick={() => navigate('/inventory')}
                >
                    <ChevronLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Create New Issuance</h1>
                    <p className="text-gray-500 font-medium text-sm">Issue uniform and PPE items to field workers and system users</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Form Area */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Recipient Details */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                                <User size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Recipient Details</h3>
                                <p className="text-gray-500 text-sm font-medium">Who is receiving the items</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Location *</label>
                                <Select value={formData.locationId} onValueChange={(val) => setFormData({ ...formData, locationId: val, items: [] })}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                        <div className="flex items-center gap-2">
                                            <MapPin size={14} className="text-gray-400" />
                                            <SelectValue placeholder="Select location" />
                                        </div>
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl">
                                        {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Worker Type *</label>
                                <Select
                                    value={formData.workerType}
                                    onValueChange={(val: any) => setFormData({ ...formData, workerType: val, workerId: '' })}
                                >
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                        <div className="flex items-center gap-2">
                                            <User size={14} className="text-gray-400" />
                                            <SelectValue placeholder="Worker Type" />
                                        </div>
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl">
                                        <SelectItem value="Field Worker">Field Worker</SelectItem>
                                        <SelectItem value="System User">System User</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Select Worker *</label>
                                <Select value={formData.workerId} onValueChange={(val) => setFormData({ ...formData, workerId: val })}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                        <SelectValue placeholder="Search workers..." />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl">
                                        {formData.workerType === 'Field Worker'
                                            ? fieldWorkers.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)
                                            : systemUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)
                                        }
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </section>

                    {/* Items to Issue */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-orange-50 rounded-xl text-orange-600">
                                    <Package size={20} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 text-lg">Items to Issue</h3>
                                    <p className="text-gray-500 text-sm font-medium">Select inventory items to issue</p>
                                </div>
                            </div>
                            <div className="relative">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="rounded-xl h-10 gap-2 border-gray-100 flex items-center px-4 font-bold"
                                    onClick={() => setShowItemResults(!showItemResults)}
                                    disabled={!formData.locationId}
                                    title={!formData.locationId ? "Please select a location first" : "Add items"}
                                >
                                    <Plus size={16} />
                                    Add Items
                                </Button>
                                {showItemResults && (
                                    <div className="absolute right-0 top-12 w-72 bg-white border border-gray-100 shadow-xl rounded-2xl z-50 p-3 space-y-1 overflow-hidden animate-in slide-in-from-top-2">
                                        <div className="relative mb-2">
                                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                            <Input
                                                autoFocus
                                                placeholder="Search stock..."
                                                className="pl-8 h-9 text-sm border-0 bg-gray-50 rounded-xl focus:ring-1"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                        <div className="max-h-60 overflow-y-auto">
                                            {filteredItems.map(item => {
                                                const outOfStock = item.currentStock === 0;
                                                return outOfStock ? (
                                                    <div
                                                        key={item.id}
                                                        className="w-full text-left p-2.5 rounded-xl flex items-center justify-between opacity-50 cursor-not-allowed"
                                                    >
                                                        <div>
                                                            <p className="font-bold text-sm text-gray-800">{item.name}</p>
                                                            <p className="text-[11px] text-red-400 font-medium">Out of stock</p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        key={item.id}
                                                        onClick={() => addItem(item)}
                                                        className="w-full text-left p-2.5 hover:bg-gray-50 rounded-xl transition-colors group flex items-center justify-between"
                                                    >
                                                        <div>
                                                            <p className="font-bold text-sm text-gray-800">{item.name}</p>
                                                            <p className="text-[11px] text-gray-400">Stock: {item.currentStock} • ${item.costPerUnit}</p>
                                                        </div>
                                                        <Plus size={14} className="text-gray-300 group-hover:text-black" />
                                                    </button>
                                                );
                                            })}
                                            {filteredItems.length === 0 && (
                                                <p className="text-xs text-gray-400 p-4 text-center">No items found</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="space-y-4">
                            {formData.items.length === 0 ? (
                                <div className="p-12 border border-blue-50 bg-blue-50/10 rounded-3xl text-center">
                                    <Package size={32} className="mx-auto text-blue-200 mb-2" />
                                    <p className="text-sm font-medium text-blue-400">No items selected to issue</p>
                                    <p className="text-xs text-blue-300 mt-1">Click Add Items to select from inventory</p>
                                </div>
                            ) : (
                                formData.items.map((item, idx) => (
                                    <div key={idx} className="p-5 bg-white border border-gray-100 rounded-2xl space-y-4 group transition-all hover:shadow-md animate-in slide-in-from-right-2">
                                        {/* Top Row: Info and Remove */}
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center shrink-0">
                                                {item.name.toLowerCase().includes('vest') ? (
                                                    <div className="w-5 h-5 flex items-center justify-center">
                                                        <div className="w-4 h-5 bg-orange-500 rounded-sm relative">
                                                            <div className="absolute top-1 left-0.5 right-0.5 h-0.5 bg-white opacity-50" />
                                                            <div className="absolute top-3 left-0.5 right-0.5 h-0.5 bg-white opacity-50" />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <HardHat size={18} className="text-orange-500" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-bold text-sm text-gray-800 truncate">{item.name}</h4>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <Badge variant="outline" className="h-4 text-[8px] px-1.5 border-gray-100 text-gray-400 uppercase tracking-tighter">
                                                        {typeof item.category === 'object' ? item.category.name : item.category}
                                                    </Badge>
                                                    <span className="text-[10px] font-medium text-gray-400">
                                                        ${parseFloat(item.unitCost).toFixed(2)} ea
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeItem(idx)}
                                                className="p-2 hover:bg-red-50 text-gray-200 hover:text-red-500 rounded-xl transition-all"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>

                                        {/* Bottom Row: Controls */}
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-3 border-t border-gray-50">
                                            <div className="flex items-center gap-4 sm:gap-6">
                                                {/* Quantity Control */}
                                                <div className="space-y-1">
                                                    <label className="block text-[8px] font-black text-gray-400 uppercase tracking-widest px-1">Quantity</label>
                                                    <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg border border-gray-100">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-6 w-6 rounded-md p-0 hover:bg-white border-0"
                                                            onClick={(e) => { e.stopPropagation(); updateQuantity(idx, -1); }}
                                                        >
                                                            <Minus size={12} className="text-gray-500" />
                                                        </Button>
                                                        <Input
                                                            type="number"
                                                            className="w-10 h-6 border-0 bg-transparent text-center font-bold text-xs p-0 focus-visible:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                            value={item.quantity}
                                                            onChange={(e) => setQuantity(idx, parseInt(e.target.value) || 0)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-6 w-6 rounded-md p-0 hover:bg-white border-0"
                                                            onClick={(e) => { e.stopPropagation(); updateQuantity(idx, 1); }}
                                                        >
                                                            <Plus size={12} className="text-gray-500" />
                                                        </Button>
                                                    </div>
                                                </div>

                                                {/* Lifespan Control */}
                                                <div className="space-y-1">
                                                    <label className="block text-[8px] font-black text-gray-400 uppercase tracking-widest px-1">Lifespan (days)</label>
                                                    <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg border border-gray-100">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-6 w-6 rounded-md p-0 hover:bg-white border-0"
                                                            onClick={(e) => { e.stopPropagation(); updateExpiryDays(idx, -1); }}
                                                        >
                                                            <Minus size={12} className="text-gray-500" />
                                                        </Button>
                                                        <Input
                                                            type="number"
                                                            className="w-10 h-6 border-0 bg-transparent text-center font-bold text-xs p-0 focus-visible:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                            value={item.expiryDays}
                                                            onChange={(e) => setExpiryDays(idx, parseInt(e.target.value) || 0)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-6 w-6 rounded-md p-0 hover:bg-white border-0"
                                                            onClick={(e) => { e.stopPropagation(); updateExpiryDays(idx, 1); }}
                                                        >
                                                            <Plus size={12} className="text-gray-500" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Subtotal */}
                                            <div className="text-left sm:text-right">
                                                <label className="block text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1">Subtotal</label>
                                                <p className="font-black text-sm text-gray-900 leading-none">
                                                    ${(item.quantity * item.unitCost).toFixed(2)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Summary Row */}
                        {formData.items.length > 0 && (
                            <div className="bg-gray-50/30 p-4 rounded-2xl flex items-center justify-between border border-gray-100/50">
                                <div className="flex items-center gap-3 text-sm font-bold text-gray-600">
                                    <div className="w-8 h-8 rounded-lg bg-white border border-gray-100 flex items-center justify-center">
                                        <Package size={16} className="text-gray-400" />
                                    </div>
                                    Total Items: {totalItems}
                                </div>
                                <div className="flex items-center gap-3 text-lg font-bold text-gray-900">
                                    <DollarSign size={20} className="text-green-500" />
                                    ${totalValue.toFixed(2)}
                                </div>
                            </div>
                        )}
                    </section>

                    {/* Signature Section */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-purple-50 rounded-xl text-purple-600">
                                <Signature size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Digital Signature</h3>
                                <p className="text-gray-500 text-sm font-medium">Capture worker acknowledgement</p>
                            </div>
                        </div>

                        <div className="space-y-3">
                            {formData.signatureData ? (
                                <div className="relative group p-4 rounded-2xl bg-gray-50/50 border border-gray-100 border-dashed flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center text-green-600">
                                            <Check size={14} />
                                        </div>
                                        {formData.signatureType === 'Draw' ? (
                                            <img src={formData.signatureData} alt="Signature" className="max-h-10" />
                                        ) : (
                                            <span className="font-signature text-xl text-gray-800">{formData.signatureData}</span>
                                        )}
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl px-3"
                                        onClick={() => setFormData({ ...formData, signatureData: '' })}
                                    >
                                        Change
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    variant="outline"
                                    className="w-full rounded-2xl border-dashed border-gray-200 h-20 flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-blue-500 hover:border-blue-500 hover:bg-blue-50/30 transition-all"
                                    onClick={() => setIsSignatureDialogOpen(true)}
                                >
                                    <Signature size={24} />
                                    <span className="text-xs font-bold uppercase tracking-widest">Capture Worker Signature</span>
                                </Button>
                            )}
                        </div>

                        {/* Signature Dialog */}
                        <Dialog open={isSignatureDialogOpen} onOpenChange={setIsSignatureDialogOpen}>
                            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md rounded-3xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle>Worker Signature</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                    <div className="flex gap-2">
                                        <Button
                                            variant={formData.signatureType === 'Type' ? 'default' : 'outline'}
                                            onClick={() => setFormData({ ...formData, signatureType: 'Type' })}
                                            className="flex-1 rounded-xl"
                                        >
                                            Type
                                        </Button>
                                        <Button
                                            variant={formData.signatureType === 'Draw' ? 'default' : 'outline'}
                                            onClick={() => setFormData({ ...formData, signatureType: 'Draw' })}
                                            className="flex-1 rounded-xl"
                                        >
                                            Draw
                                        </Button>
                                    </div>

                                    {formData.signatureType === 'Type' ? (
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-gray-700">Enter full name:</label>
                                            <Input
                                                value={typedSignature}
                                                onChange={(e) => setTypedSignature(e.target.value)}
                                                placeholder="Your Full Name"
                                                className="font-signature text-lg rounded-xl h-12"
                                            />
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold text-gray-700">Draw signature:</label>
                                            <canvas
                                                ref={canvasRef}
                                                width={400}
                                                height={150}
                                                className="w-full border rounded-2xl cursor-crosshair bg-gray-50"
                                                onMouseDown={startDrawing}
                                                onMouseMove={draw}
                                                onMouseUp={stopDrawing}
                                                onMouseLeave={stopDrawing}
                                                onTouchStart={handleTouchStart}
                                                onTouchMove={handleTouchMove}
                                                onTouchEnd={stopDrawing}
                                            />
                                            <Button variant="outline" size="sm" onClick={clearCanvas} className="rounded-lg">
                                                Clear
                                            </Button>
                                        </div>
                                    )}

                                    <div className="flex justify-end gap-2 pt-4">
                                        <Button variant="outline" onClick={() => setIsSignatureDialogOpen(false)} className="rounded-xl">Cancel</Button>
                                        <Button onClick={handleConfirmSignature} className="bg-black text-white rounded-xl">Confirm Signature</Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </section>

                    {/* Notes */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-gray-50 rounded-xl text-gray-600">
                                <Package size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Notes</h3>
                                <p className="text-gray-500 text-sm font-medium">Optional notes about this issuance</p>
                            </div>
                        </div>
                        <textarea
                            className="w-full h-24 rounded-2xl border-gray-100 bg-gray-50/50 p-4 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-blue-500 transition-all resize-none placeholder:text-gray-400"
                            placeholder="Add any notes about this issuance..."
                            value={formData.notes}
                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        />
                    </section>
                </div>

                {/* Sidebar / Actions */}
                <div className="lg:col-span-1">
                    <div className="sticky top-6 space-y-6">
                        <section className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <h3 className="font-bold text-gray-900">Actions</h3>
                            <div className="space-y-3">
                                <Button
                                    className="w-full rounded-2xl h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm gap-2 shadow-sm transition-all active:scale-95 disabled:opacity-50"
                                    onClick={handleSubmit}
                                    disabled={loading || formData.items.length === 0 || !formData.signatureData}
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Send size={16} />
                                            Create Issuance
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-full rounded-2xl h-12 font-bold text-sm border-gray-100"
                                    onClick={() => navigate('/inventory')}
                                >
                                    Cancel
                                </Button>
                            </div>
                            <div className="pt-4 border-t border-gray-50 space-y-2">
                                <p className="text-xs text-gray-400 font-medium">
                                    Make sure all required fields are filled and at least one item is selected.
                                </p>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateIssuance;
